import React, { useEffect, useState, useMemo } from 'react';
import {
  Users, Plus, X, Loader, CheckCircle, AlertCircle, Phone, Mail,
  GitBranch, UserPlus, TrendingUp, Award, Star, ShoppingBag
} from 'lucide-react';
import {
  getCrmBoard, captureLead, moveLeadStage, getCrmContacts
} from '../services/api';
import './MarketingCRM.css';

const fmt = (n) => (n || n === 0 ? new Intl.NumberFormat('fr-FR').format(n) + ' XOF' : '—');

// Couleurs par segment RFM
const SEGMENTS = {
  champion:   { label: 'Champion',   color: '#7c3aed', bg: '#ede9fe' },
  fidele:     { label: 'Fidèle',     color: '#2563eb', bg: '#dbeafe' },
  gros_panier:{ label: 'Gros panier',color: '#0891b2', bg: '#cffafe' },
  nouveau:    { label: 'Nouveau',    color: '#16a34a', bg: '#dcfce7' },
  a_risque:   { label: 'À risque',   color: '#d97706', bg: '#fef3c7' },
  perdu:      { label: 'Perdu',      color: '#dc2626', bg: '#fee2e2' },
  regulier:   { label: 'Régulier',   color: '#475569', bg: '#f1f5f9' },
};

const SOURCES = {
  form: 'Formulaire', csv: 'Import CSV', qr: 'QR code',
  whatsapp: 'WhatsApp', erp: 'Vente', manual: 'Manuel',
};

const EMPTY_FORM = { nom: '', telephone: '', email: '', source: 'manual', locale: 'fr' };

const SegmentBadge = ({ rfm }) => {
  if (!rfm || !rfm.segment) return <span className="mk-badge mk-badge--muted">Sans score</span>;
  const s = SEGMENTS[rfm.segment] || SEGMENTS.regulier;
  return <span className="mk-badge" style={{ color: s.color, background: s.bg }}>{s.label}</span>;
};

export default function MarketingCRM() {
  const [tab, setTab] = useState('pipeline');
  const [board, setBoard] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 3500); };

  const loadBoard = async () => {
    try {
      setLoading(true);
      const res = await getCrmBoard();
      setBoard(res.data.data);
    } catch { showToast('error', 'Erreur de chargement du pipeline'); }
    finally { setLoading(false); }
  };

  const loadContacts = async () => {
    try {
      setLoading(true);
      const res = await getCrmContacts();
      setContacts(res.data.data || []);
    } catch { showToast('error', 'Erreur de chargement des contacts'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'pipeline') loadBoard(); else loadContacts();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const allStages = useMemo(
    () => (board?.colonnes || []).map((c) => c.stage),
    [board]
  );

  const totalLeads = useMemo(
    () => (board?.colonnes || []).reduce((s, c) => s + c.leads.length, 0),
    [board]
  );

  const handleCapture = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await captureLead(form);
      showToast('success', 'Lead capturé');
      setShowModal(false);
      setForm(EMPTY_FORM);
      if (tab === 'pipeline') loadBoard(); else loadContacts();
    } catch (err) {
      showToast('error', err.response?.data?.message || err.response?.data?.errors?.[0]?.message || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const handleMove = async (leadId, stageId) => {
    try {
      await moveLeadStage(leadId, stageId);
      loadBoard();
    } catch (err) { showToast('error', err.response?.data?.message || 'Déplacement impossible'); }
  };

  return (
    <div className="mk-page">
      {toast && (
        <div className={`mk-toast mk-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{toast.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="mk-header">
        <div>
          <h1 className="mk-title"><Users size={26} /> CRM — Funnel commercial</h1>
          <p className="mk-subtitle">Capture multi-source, scoring RFM et conversion automatique des leads.</p>
        </div>
        <button className="mk-btn mk-btn--primary" onClick={() => setShowModal(true)}>
          <UserPlus size={18} /> Capturer un lead
        </button>
      </div>

      {/* Tabs */}
      <div className="mk-tabs">
        <button className={`mk-tab ${tab === 'pipeline' ? 'mk-tab--active' : ''}`} onClick={() => setTab('pipeline')}>
          <GitBranch size={16} /> Pipeline {totalLeads ? `(${totalLeads})` : ''}
        </button>
        <button className={`mk-tab ${tab === 'contacts' ? 'mk-tab--active' : ''}`} onClick={() => setTab('contacts')}>
          <Users size={16} /> Contacts {contacts.length ? `(${contacts.length})` : ''}
        </button>
      </div>

      {loading && (
        <div className="mk-loading"><Loader className="mk-spin" size={28} /> Chargement…</div>
      )}

      {/* Pipeline Kanban */}
      {!loading && tab === 'pipeline' && board && (
        <div className="mk-board">
          {board.colonnes.map(({ stage, leads }) => (
            <div key={stage.id} className="mk-col">
              <div className={`mk-col-head mk-col-head--${stage.kind}`}>
                <span>{stage.nom}</span>
                <span className="mk-col-count">{leads.length}</span>
              </div>
              <div className="mk-col-body">
                {leads.length === 0 && <div className="mk-empty">Aucun lead</div>}
                {leads.map((lead) => (
                  <div key={lead.id} className="mk-card">
                    <div className="mk-card-top">
                      <strong>{lead.contact?.nom || 'Contact'}</strong>
                      <SegmentBadge rfm={lead.contact?.rfm} />
                    </div>
                    {lead.contact?.phone && (
                      <div className="mk-card-line"><Phone size={13} /> {lead.contact.phone}</div>
                    )}
                    <div className="mk-card-foot">
                      <span className="mk-src">{SOURCES[lead.source] || lead.source}</span>
                      <select
                        className="mk-move"
                        value={stage.id}
                        onChange={(e) => handleMove(lead.id, e.target.value)}
                        title="Déplacer vers une étape"
                      >
                        {allStages.map((s) => (
                          <option key={s.id} value={s.id}>{s.nom}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Contacts table */}
      {!loading && tab === 'contacts' && (
        <div className="mk-table-wrap">
          {contacts.length === 0 ? (
            <div className="mk-empty-big"><Users size={40} /><p>Aucun contact pour l'instant.</p></div>
          ) : (
            <table className="mk-table">
              <thead>
                <tr>
                  <th>Contact</th><th>Téléphone</th><th>Segment RFM</th>
                  <th>R / F / M</th><th>Commandes</th><th>Total dépensé</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="mk-contact-name">{c.nom}</div>
                      {c.email && <div className="mk-contact-sub"><Mail size={12} /> {c.email}</div>}
                    </td>
                    <td>{c.phone || '—'}</td>
                    <td><SegmentBadge rfm={c.rfm} /></td>
                    <td>
                      {c.rfm ? (
                        <span className="mk-rfm">
                          <span title="Récence"><TrendingUp size={12} />{c.rfm.recency}</span>
                          <span title="Fréquence"><Star size={12} />{c.rfm.frequency}</span>
                          <span title="Monétaire"><Award size={12} />{c.rfm.monetary}</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td><span className="mk-orders"><ShoppingBag size={13} /> {c.ordersCount || 0}</span></td>
                    <td className="mk-money">{fmt(c.totalSpent?.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal capture */}
      {showModal && (
        <div className="mk-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="mk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mk-modal-head">
              <h3><UserPlus size={20} /> Capturer un lead</h3>
              <button className="mk-icon-btn" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCapture} className="mk-form">
              <label>Nom *
                <input value={form.nom} required onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Awa Diop" />
              </label>
              <label>Téléphone *
                <input value={form.telephone} required onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="77 123 45 67" />
              </label>
              <label>Email
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="(optionnel)" />
              </label>
              <div className="mk-form-row">
                <label>Source
                  <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                    {['manual', 'form', 'qr', 'whatsapp', 'csv'].map((s) => <option key={s} value={s}>{SOURCES[s]}</option>)}
                  </select>
                </label>
                <label>Langue
                  <select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}>
                    <option value="fr">Français</option>
                    <option value="wo">Wolof</option>
                  </select>
                </label>
              </div>
              <div className="mk-modal-foot">
                <button type="button" className="mk-btn mk-btn--ghost" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="mk-btn mk-btn--primary" disabled={submitting}>
                  {submitting ? <Loader className="mk-spin" size={16} /> : <Plus size={16} />} Capturer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
